import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { toast } from 'sonner';
import { 
  Check, 
  X, 
  Crown, 
  Shield, 
  Zap,
  Sparkles,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const PricingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const plansRes = await axios.get(`${API}/subscription/plans`);
      setPlans(plansRes.data);
      
      if (token) {
        try {
          const subRes = await axios.get(`${API}/subscription/subscription/current`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCurrentSubscription(subRes.data);
        } catch (e) {
          console.log('No active subscription');
        }
      }
    } catch (error) {
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (planId) => {
    if (!user) {
      toast.error('Debes iniciar sesión para suscribirte');
      navigate('/');
      return;
    }
    const plan = plans.find(p => p.id === planId);
    setSelectedPlan(plan);
    setPaymentModalOpen(true);
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'basic': return Shield;
      case 'premium': return Crown;
      case 'vip': return Zap;
      default: return Shield;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'basic': return 'from-slate-500 to-slate-700';
      case 'premium': return 'from-blue-500 to-blue-700';
      case 'vip': return 'from-amber-500 to-amber-700';
      default: return 'from-slate-500 to-slate-700';
    }
  };

  const getPlanBorderColor = (planId) => {
    switch (planId) {
      case 'basic': return 'border-slate-600';
      case 'premium': return 'border-blue-500';
      case 'vip': return 'border-amber-500';
      default: return 'border-slate-600';
    }
  };

  const getPrice = (plan) => {
    return billingCycle === 'yearly' ? plan.price_yearly : plan.price;
  };

  const getSavings = (plan) => {
    if (billingCycle === 'yearly') {
      const monthlyTotal = plan.price * 12;
      const savings = monthlyTotal - plan.price_yearly;
      return Math.round(savings);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      {user && <Navbar />}
      
      <main className={`${user ? 'pt-20' : 'pt-8'} px-4 max-w-6xl mx-auto`}>
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-green-600/20 text-green-400 border-green-500/30">
            Planes de Suscripción
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4" data-testid="pricing-title">
            Elige tu Plan
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Accede a análisis avanzados y recomendaciones inteligentes para todas las loterías dominicanas
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
            <TabsList className="bg-slate-900 border border-slate-800">
              <TabsTrigger 
                value="monthly" 
                className="data-[state=active]:bg-green-600"
                data-testid="billing-monthly"
              >
                Mensual
              </TabsTrigger>
              <TabsTrigger 
                value="yearly" 
                className="data-[state=active]:bg-green-600"
                data-testid="billing-yearly"
              >
                Anual
                <Badge className="ml-2 bg-amber-500/20 text-amber-400 text-xs">Ahorra 17%</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan, idx) => {
            const Icon = getPlanIcon(plan.id);
            const isCurrentPlan = currentSubscription?.subscription?.plan_id === plan.id;
            const isPopular = plan.id === 'premium';
            
            return (
              <Card 
                key={plan.id}
                className={`glass-card relative overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
                  isPopular ? 'border-2 border-blue-500' : getPlanBorderColor(plan.id)
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
                data-testid={`plan-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-br-lg">
                    TU PLAN
                  </div>
                )}
                
                <CardHeader className="text-center pb-0">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getPlanColor(plan.id)} flex items-center justify-center mx-auto mb-4`}>
                    <Icon size={32} className="text-white" />
                  </div>
                  <CardTitle className="text-white text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-400">{plan.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="pt-6">
                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-slate-400">$</span>
                      <span className="text-4xl font-black text-white font-mono">
                        {getPrice(plan).toFixed(2)}
                      </span>
                      <span className="text-slate-400">/{billingCycle === 'yearly' ? 'año' : 'mes'}</span>
                    </div>
                    {billingCycle === 'yearly' && getSavings(plan) > 0 && (
                      <p className="text-green-400 text-sm mt-1">
                        Ahorras ${getSavings(plan)} al año
                      </p>
                    )}
                  </div>
                  
                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    <FeatureItem 
                      available={true}
                      text={plan.features.max_lotteries === -1 
                        ? 'Todas las loterías' 
                        : `${plan.features.max_lotteries} lotería${plan.features.max_lotteries > 1 ? 's' : ''}`}
                    />
                    <FeatureItem 
                      available={true}
                      text={plan.features.daily_recommendations === -1 
                        ? 'Recomendaciones ilimitadas' 
                        : `${plan.features.daily_recommendations} recomendación${plan.features.daily_recommendations > 1 ? 'es' : ''}/día`}
                    />
                    <FeatureItem 
                      available={plan.features.strategies.includes('conservative')}
                      text="Estrategia Conservadora"
                    />
                    <FeatureItem 
                      available={plan.features.strategies.includes('balanced')}
                      text="Estrategia Balanceada"
                    />
                    <FeatureItem 
                      available={plan.features.strategies.includes('risky')}
                      text="Estrategia Arriesgada"
                    />
                    <FeatureItem 
                      available={plan.features.hot_cold_access}
                      text="Números Calientes/Fríos"
                    />
                    <FeatureItem 
                      available={plan.features.advanced_combos}
                      text="Combinaciones Avanzadas"
                    />
                    <FeatureItem 
                      available={plan.features.detailed_analysis}
                      text="Análisis Detallado"
                    />
                    <FeatureItem 
                      available={true}
                      text={plan.features.history_days === -1 
                        ? 'Historial completo' 
                        : `Historial de ${plan.features.history_days} días`}
                    />
                  </ul>
                  
                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isCurrentPlan}
                    className={`w-full py-6 font-bold ${
                      isCurrentPlan 
                        ? 'bg-green-600/30 text-green-400 cursor-default'
                        : plan.id === 'vip' 
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500'
                          : plan.id === 'premium'
                            ? 'bg-blue-600 hover:bg-blue-500'
                            : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {isCurrentPlan ? (
                      <span className="flex items-center gap-2">
                        <Check size={20} />
                        Plan Actual
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Sparkles size={20} />
                        Suscribirse
                        <ArrowRight size={20} />
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Comparison Table for Desktop */}
        <Card className="glass-card hidden lg:block" data-testid="comparison-table">
          <CardHeader>
            <CardTitle className="text-white text-center">Comparación de Planes</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left text-slate-400 py-4 px-4">Característica</th>
                  <th className="text-center text-slate-400 py-4 px-4">Básico</th>
                  <th className="text-center text-slate-400 py-4 px-4">Premium</th>
                  <th className="text-center text-slate-400 py-4 px-4">VIP</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <ComparisonRow feature="Loterías" basic="1" premium="3" vip="Todas" />
                <ComparisonRow feature="Recomendaciones/día" basic="1" premium="4" vip="Ilimitadas" />
                <ComparisonRow feature="Estrategia Conservadora" basic={true} premium={true} vip={true} />
                <ComparisonRow feature="Estrategia Balanceada" basic={false} premium={true} vip={true} />
                <ComparisonRow feature="Estrategia Arriesgada" basic={false} premium={false} vip={true} />
                <ComparisonRow feature="Números Calientes/Fríos" basic={false} premium={true} vip={true} />
                <ComparisonRow feature="Combinaciones Frecuentes" basic={false} premium={true} vip={true} />
                <ComparisonRow feature="Análisis por Tendencias" basic={false} premium={true} vip={true} />
                <ComparisonRow feature="Historial" basic="7 días" premium="30 días" vip="Completo" />
                <ComparisonRow feature="Precio Mensual" basic="$19.99" premium="$39.99" vip="$59.99" />
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-400 mb-2">
            <AlertCircle size={18} />
            <span className="text-sm font-medium">Aviso Legal</span>
          </div>
          <p className="text-slate-400 text-sm max-w-2xl mx-auto">
            Las recomendaciones se basan en análisis estadístico y no garantizan resultados. 
            Cada sorteo es un evento independiente. Juegue responsablemente.
          </p>
        </div>
      </main>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        plan={selectedPlan}
        billingCycle={billingCycle}
      />
    </div>
  );
};

const FeatureItem = ({ available, text }) => (
  <li className="flex items-center gap-3">
    {available ? (
      <Check size={18} className="text-green-400 flex-shrink-0" />
    ) : (
      <X size={18} className="text-slate-600 flex-shrink-0" />
    )}
    <span className={available ? 'text-slate-300' : 'text-slate-600'}>{text}</span>
  </li>
);

const ComparisonRow = ({ feature, basic, premium, vip }) => {
  const renderValue = (val) => {
    if (typeof val === 'boolean') {
      return val ? (
        <Check size={18} className="text-green-400 mx-auto" />
      ) : (
        <X size={18} className="text-slate-600 mx-auto" />
      );
    }
    return <span className="text-white">{val}</span>;
  };

  return (
    <tr className="border-b border-slate-800/50">
      <td className="text-slate-300 py-4 px-4">{feature}</td>
      <td className="text-center py-4 px-4">{renderValue(basic)}</td>
      <td className="text-center py-4 px-4">{renderValue(premium)}</td>
      <td className="text-center py-4 px-4">{renderValue(vip)}</td>
    </tr>
  );
};

export default PricingPage;
